<template>
  <v-container
    class="primary lighten-2 white--text pa-0 rounded-0 overflow-hidden"
    fluid
    style="height: 100%; position: relative"
  >
    <l-map
      :zoom="zoom"
      :center="center"
      :options="{ attributionControl: true, zoomControl: false }"
      ref="mainMap"
      :min-zoom="3"
      @update:center="sendCenterToStore"
    >
      <l-tile-layer :url="url" :attribution="attribution"></l-tile-layer>
      <l-marker-cluster
        :options="{
          showCoverageOnHover: false,
        }"
        id="pwt-marker-cluster-main"
      >
        <template v-for="(chargingStation, index) in filteredStations">
          <ChargingPin
            v-if="!!chargingStation.coordinates"
            :key="`charger-pin-${index}-${chargingStation.id}`"
            :chargingStation="chargingStation"
            :isChargingStop="getIsChargingStop(chargingStation.id)"
          />
        </template>
      </l-marker-cluster>
      <GradientPolyline
        v-for="(polyline, index) in polylineList"
        :key="`gradient-polyline-${index}`"
        :latLngList="polyline"
        :startCharge="
          chargeData.length ? chargeData[index].StartCharge : undefined
        "
        :endCharge="chargeData.length ? chargeData[index].EndCharge : undefined"
        :hasNoChargeData="!chargeData.length"
        :stepIndex="index"
        @closePopups="closePopups"
      />
      <template v-if="!!tripDetails">
        <LocationPin
          v-for="(location, index) in tripDetails.locations"
          :key="location.id"
          :locationData="location"
          :number="index"
          :showStar="getIsStarLocation(index)"
        />
      </template>
      <template v-if="showingConnectedVehicles">
        <template v-for="(vehicle, index) in connectedVehicles">
          <CarPin
            v-if="vehicle && vehicle.latitude && vehicle.longitude"
            :key="`car-pin-${index}`"
            :location="getGeoLocation(vehicle.latitude, vehicle.longitude)"
            :id="vehicle.localId"
          />
        </template>
      </template>
    </l-map>
    <BottomLogo></BottomLogo>
  </v-container>
</template>

<script lang="ts">
import Vue from "vue";
import { LMap, LTileLayer } from "vue2-leaflet";
import { mapGetters, mapState } from "vuex";
import ChargingPin from "./ChargingPin.vue";
import CarPin from "./CarPin.vue";
import GradientPolyline from "./GradientPolyline.vue";
import LocationPin from "./LocationPin.vue";
import {
  GettersTypes,
  MutationTypes,
  type State,
} from "@/logic/store/store_types";
import Vue2LeafletMarkerCluster from "vue2-leaflet-markercluster";
import Charger from "@/logic/classes/charger_classes/charger";
import BottomLogo from "../logo/BottomLogo.vue";
import Coordinate from "@/logic/classes/common_classes/coordinate";
import Trip from "@/logic/classes/trip_classes/trip";

/**
 * `Vue leaflet component:` the main container for rendering the map and all its children components
 */
export default Vue.extend({
  name: "MapPanel",
  components: {
    LMap,
    LTileLayer,
    ChargingPin,
    CarPin,
    GradientPolyline,
    LocationPin,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    "l-marker-cluster": Vue2LeafletMarkerCluster,
    BottomLogo,
  },
  data() {
    return {
      url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      attribution:
        '&copy; <a target="_blank" href="http://osm.org/copyright">OpenStreetMap</a> contributors',
      zoom: 7,
      center: [-41.270634, 173.283966],
    };
  },
  computed: {
    ...mapGetters({
      filteredStations: GettersTypes.filterChargingStations,
      tripDetails: GettersTypes.selectedTripData,
      connectedVehicles: GettersTypes.connectedVehicles,
    }),
    polylineList(): [number, number][][] {
      const trip: Trip | undefined =
        this.$store.getters[GettersTypes.selectedTripData];
      if (!trip) return [];

      // convert steps to leaflet polyline lat-longs polyline property for conditional rendering.
      const convertedArray = trip.getPolylinePoints();
      // get full list of lat-longs for map centering
      const collapsedArray = convertedArray.flat();

      // center map
      this.reCenterMap(collapsedArray);

      // return array of polyline lat-longs list for conditional rendering.
      return convertedArray;
    },
    chargeData(): { StartCharge: number; EndCharge: number }[] {
      const trip: Trip | undefined =
        this.$store.getters[GettersTypes.selectedTripData];
      if (!trip) return [];
      if (trip instanceof Trip) {
        return (
          trip.itinerary.steps.map((step) => ({
            StartCharge: step.chargeBeforeTraveling,
            EndCharge: step.chargeAfterTraveling,
          })) ?? []
        );
      }
      return [];
    },
    ...mapState({
      highLightLongestLeap: (state: unknown): boolean =>
        (state as State).displayLongestStep,
      showingConnectedVehicles: (state: unknown) =>
        (state as State).displayConnectedVehicles,
      userGeoLocation: (state: unknown): Coordinate | undefined =>
        (state as State).userGeoIPData,
      showingNearbyChargers: (state: unknown): boolean =>
        (state as State).showNearbyChargersOnly,
    }),
  },
  mounted() {
    window.addEventListener("resize", this.resizeMap);
    this.$nextTick(() => {
      if (this.userGeoLocation && this.userGeoLocation.isValid) {
        this.center = this.userGeoLocation.asLatLng;
        // center map
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (this.$refs.mainMap as any)?.mapObject.flyTo(
          this.userGeoLocation.asLatLng
        );
      }
    });
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.resizeMap);
  },
  methods: {
    sendCenterToStore(val: { lat: number; lng: number }) {
      this.$store.commit(MutationTypes.setPannedCenter, val);
    },
    getIsStarLocation(index: number): boolean {
      // check for cases where location is origin or destination.
      if (index === 0) return true; //case: origin. NOTE: if round trip this will also be the destination.
      if (
        !(this.tripDetails as Trip).roundTripFlag &&
        index === (this.tripDetails as Trip).locations.length - 1
      )
        return true; //case: destination.

      // case: not an origin or a destination.
      return false;
    },
    resizeMap() {
      // timeout used to let browser handle resize animation before resetting
      setTimeout(() => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (this.$refs.mainMap as any)?.mapObject.invalidateSize();
      }, 200);
    },
    getGeoLocation(latitude: number, longitude: number): Coordinate {
      return new Coordinate({ latitude, longitude });
    },
    reCenterMap(pointsList: [number, number][]) {
      // get map padding (40% in pixels)
      let options = {
        paddingTopLeft: [(this.$vuetify.breakpoint.width / 100) * 40, 0],
      };

      // center map
      if (pointsList.length)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (this.$refs.mainMap as any)?.mapObject.fitBounds(pointsList, options);
    },
    /**
     * Check if given charger id is flagged as a charging stop on the displayed trip.
     *
     * @param chargerID the charger database UUID for the charger.
     * @returns true if the charger is flagged false if not.
     */
    getIsChargingStop(chargerID: string): boolean {
      if (!this.tripDetails) return false; // fail fast guard clause.
      return this.tripDetails.chargingStopCDBIDs.includes(chargerID); // return true if id is included.
    },
    closePopups() {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (this.$refs.mainMap as any)?.mapObject.closePopup();
    },
  },
  watch: {
    showingNearbyChargers(val) {
      if (val) {
        const chargerLatLngList = (this.filteredStations as Charger[]).map(
          (charger) => charger.coordinates.asLatLng
        );
        // center map
        this.reCenterMap(chargerLatLngList);
      }
    },
  },
});
</script>
<style>
@import "~leaflet.markercluster/dist/MarkerCluster.css";
/* @import "~leaflet.markercluster/dist/MarkerCluster.Default.css"; */

/* copied stiles from  "~leaflet.markercluster/dist/MarkerCluster.Default.css" for better control over colors*/
.marker-cluster-small {
  background-color: #42a5f5;
}

.marker-cluster-small div {
  background-color: #2196f3;
}

.marker-cluster-medium {
  background-color: #1e88e5;
}

.marker-cluster-medium div {
  background-color: #1976d2;
}

.marker-cluster-large {
  background-color: #1565c0;
}

.marker-cluster-large div {
  background-color: #0d47a1;
}

.marker-cluster {
  background-clip: padding-box;
  border-radius: 20px;
}

.marker-cluster div {
  width: 30px;
  height: 30px;
  margin-left: 5px;
  margin-top: 5px;
  text-align: center;
  border-radius: 15px;
  font:
    12px "Helvetica Neue",
    Arial,
    Helvetica,
    sans-serif;
}

.marker-cluster span {
  line-height: 30px;
}
</style>
